import { useEffect, useRef, useState } from "react";
import { Star, MapPin, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface GoogleReviewsSectionProps {
  widgetId?: string;
  className?: string;
}

const GOOGLE_MAPS_URL = "https://share.google/yXybM7SyeflUp7leT";

const GoogleReviewsSection = ({
  widgetId = "3ae491b2-5b77-4d2a-800b-543a4a52c1f4",
  className,
}: GoogleReviewsSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  // Lazy load widget when section becomes visible - increased threshold for better performance
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "50px", threshold: 0.01 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load Elfsight script when section becomes visible - deferred loading for performance
  useEffect(() => {
    if (!isVisible || scriptLoadedRef.current) return;

    const loadScript = () => {
      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src*="elfsight"]');
      
      if (existingScript) {
        scriptLoadedRef.current = true;
        setTimeout(() => setIsLoaded(true), 500);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://static.elfsight.com/platform/platform.js";
      script.async = true;
      script.onload = () => {
        scriptLoadedRef.current = true;
        setTimeout(() => setIsLoaded(true), 800);
      };
      document.body.appendChild(script);
    };

    // Delay loading significantly to prioritize above-the-fold content
    // Use setTimeout with 1s delay to ensure main content renders first
    const timeoutId = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback(loadScript, { timeout: 5000 });
      } else {
        // Fallback for Safari
        setTimeout(loadScript, 500);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      className={cn("py-20 bg-white", className)}
      aria-labelledby="reviews-heading"
    >
      <div className="container mx-auto px-4">
        <header className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
            RECENZE ZÁKAZNÍKŮ
          </span>
          <h2
            id="reviews-heading"
            className="text-3xl md:text-4xl font-bold mb-4 text-foreground"
          >
            Co říkají naši <span className="text-primary">zákazníci</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Přečtěte si hodnocení od spokojených klientů z celého Teplicka
          </p>
        </header>

        {/* Widget wrapper with professional styling */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-3xl p-6 md:p-10 shadow-xl shadow-primary/5">
            {/* Fallback UI while widget loads */}
            {!isLoaded && (
              <div className="text-center py-8" role="status" aria-label="Načítám recenze">
                {/* Star rating preview */}
                <div className="flex justify-center gap-1.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-8 w-8 text-primary fill-primary"
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <p className="text-2xl font-bold text-foreground mb-2">
                  5.0 hodnocení
                </p>
                <p className="flex items-center justify-center gap-2 text-muted-foreground mb-6">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  na Google Maps
                </p>

                {/* Loading skeleton */}
                <div className="grid md:grid-cols-3 gap-4 mt-8">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-background rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  ))}
                </div>

                <p className="text-sm text-muted-foreground mt-6 animate-pulse">
                  Načítám recenze...
                </p>
              </div>
            )}

            {/* Elfsight Widget Container */}
            {isVisible && (
              <div
                className={cn(
                  "transition-opacity duration-500 [&_.eapps-widget]:!font-sans",
                  isLoaded ? "opacity-100" : "opacity-0 absolute pointer-events-none"
                )}
              >
                <div
                  className={`elfsight-app-${widgetId}`}
                  data-elfsight-app-lazy
                />
              </div>
            )}
          </div>

          {/* CTA link to Google Maps */}
          <footer className="text-center mt-8">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              <span>Zobrazit více na Google Maps</span>
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          </footer>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
